/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { computed } from 'mobx';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ICittaNewsletterSubscribeData } from './citta-newsletter-subscribe.data';
import { ICittaNewsletterSubscribeProps } from './citta-newsletter-subscribe.props.autogenerated';
import { SetCookie } from '../../shared-utils/cookie-utilities';

interface ICittaNewsletteSubscriberFormState {
    emailAddress: string;
    privacyPolicyChecked: boolean;
    eAValError: boolean;
    eAError: boolean;
    pPCError: boolean;
    rCCError: boolean;
    reqError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    responseSuccess: boolean;
    reCaptchaChecked: boolean;
}

export interface ICittaSubscribeNewsletterForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    emailInput: React.ReactNode;
    checkboxPrivacyPolicy: React.ReactNode;
    reCaptcha: React.ReactNode;
    subscribeButtton: React.ReactNode;
    subscribeThanks: React.ReactNode;
}

export interface ICittaNewsletterSubscribeViewProps extends ICittaNewsletterSubscribeProps<ICittaNewsletterSubscribeData> {
    SubscribeWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    subscribeForm: ICittaSubscribeNewsletterForm;
    responseSuccess: boolean;
    userEmail: React.ReactNode;
}

/**
 *
 * CittaNewsletterSubscribe component
 * @extends {React.PureComponent<ICittaNewsletterSubscribeProps<ICittaNewsletterSubscribeData>>}
 */

class CittaNewsletterSubscribe extends React.PureComponent<ICittaNewsletterSubscribeProps<ICittaNewsletterSubscribeData>, ICittaNewsletteSubscriberFormState> {
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _PrivacyPolicyRef: React.RefObject<HTMLInputElement>;
    private _recaptchaRef: React.RefObject<ReCAPTCHA>;

    private emailAddressValue: boolean = false;

    private formInputValues: boolean = false;

    private responseMessage: string | undefined;

    private moduleClassName: string = 'newsletterSubWrap';

    private _newsletterCookieName: string = 'cittaSignUp';

    private recaptchaErrorMessage: string | undefined;

    @computed public get userEmail(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.Email : '';
    }

    constructor(props: ICittaNewsletterSubscribeProps<ICittaNewsletterSubscribeData>) {
        super(props);
        this._EmailAddressInputRef = React.createRef();
        this._PrivacyPolicyRef = React.createRef();
        this._recaptchaRef = React.createRef();
        this._handleSubscribe = this._handleSubscribe.bind(this);

        this.state = {
            emailAddress: '',
            privacyPolicyChecked: false,
            eAValError: false,
            eAError: false,
            pPCError: false,
            rCCError: false,
            reqError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false,
            responseSuccess: false
        };
    }

    public render(): JSX.Element | null {
        const viewProps: ICittaNewsletterSubscribeViewProps = {
            ...(this.props as ICittaNewsletterSubscribeProps<ICittaNewsletterSubscribeData>),
            SubscribeWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName)
            },
            subscribeResponse: this._getSubscribeResponse(),
            subscribeForm: this._renderForm(),
            responseSuccess: this.state.responseSuccess,
            userEmail: this._renderHiddenEmailAddress()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderHiddenEmailAddress(): React.ReactNode {
        return (
           this.userEmail && 
                <span style={{display: "none"}} className='emarsys-user-email'>{this.userEmail}</span>
        );
    }
    
    private _getSubscribeResponse(): React.ReactNode {
        return (
            this.state.responseReceived && this.responseMessage !== '' || undefined
                ? (
                    <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                        <p className='response-message-content'>{this.responseMessage}</p>
                    </div>
                )
                : null
        );
    }

    private onRecaptchaChange = (value: string | null) => {
        if (value) {
            this.setState({ reCaptchaChecked: true, rCCError: false });
        } else {
            this.setState({ reCaptchaChecked: false });
        }
    }

    private _renderForm(): ICittaSubscribeNewsletterForm {
        return {
            FormWrapper: {
                className: 'newsletter-subscription',
            },
            FormContainer: {
                className: 'newsletter-subscription-div',
            },
            emailInput: this._renderEmailInput(),
            checkboxPrivacyPolicy: this._renderCheckboxPrivacyPolicy(),
            reCaptcha: this._renderReCaptcha(),
            subscribeButtton: this._renderSubscribeButton(),
            subscribeThanks: this._renderSubscribeThanks()
        };
    }

    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    value={this.state.emailAddress}
                    required
                    placeholder={this.props.config.emailInputPlaceholder}
                />
            </div>
        );
    }

    private _renderAlert(className: string): JSX.Element {
        return <></>;
    }

    private _renderCheckboxPrivacyPolicy(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                {this.state.pPCError ? this._renderAlert('checkbox-privacy-policy') : ''}
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._PrivacyPolicyRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Privacy Policy'
                        aria-checked='false'
                        onChange={this._handleChange('privacyPolicy')}
                        id='privacyPolicyCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>{this.props.config.privacyPolicyText}<a href={this.props.config.privacyPolicyLink}>Privacy Policy</a></p>
                    </span>
                </div>
            </div>
        );
    }

    private _renderReCaptcha(): React.ReactNode {
        return (
            <div>
                {
                    this.props.config.reCaptchaOption ? <div className='recaptcha newsletter-recapture-visable'>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            onChange={this.onRecaptchaChange}
                        />
                    </div> : null
                }
            </div>
        );
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _renderSubscribeButton(): React.ReactNode {
        return (
            <div>
                <Button
                    className={`subscribe-button`}
                    title={this.props.config.subscribeButtonLabel}
                    color='primary'
                    onClick={this._handleSubscribe}
                >{this.props.config.subscribeButtonLabel}
                </Button>
            </div>
        );
    }

    private _renderSubscribeThanks(): React.ReactNode {
        return (
            <div>
                <div className={`subscribe-thanks`}>
                    Thanks!
                </div>
            </div>
        );
    }

    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'emailAddress': {
                this.setState({ emailAddress: value, eAError: false, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'privacyPolicy': {
                const isChecked = this._PrivacyPolicyRef.current && this._PrivacyPolicyRef.current.checked ? this._PrivacyPolicyRef.current.checked : false;
                const recaptchaWrapper = document.querySelector('.recaptcha');
                recaptchaWrapper && recaptchaWrapper.classList.toggle('newsletter-recapture-visable', !isChecked);
                this.setState({ privacyPolicyChecked: isChecked, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false, pPCError: false });
            }
        }
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private async _handleSubscribe(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> {
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;

        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';
        const eAVal = this._EmailAddressInputRef.current!.value;

        if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
        } else if (!this._isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
        } else if (this.props.config.reCaptchaOption && !this.state.reCaptchaChecked) {
            this.setState({
                rCCError: true
            });
        } else if (!this._PrivacyPolicyRef.current!.checked) {
            this.setState({
                privacyPolicyChecked: false,
                pPCError: true
            });
        } else {
            this.formInputValues = (this.emailAddressValue) ? true : false;
            if (this.formInputValues && this.state.privacyPolicyChecked && !this.state.eAValError) {
                try {
                    await this._postEmailContact(eA);

                    this._clearValues();
                    this._recaptchaRef.current?.reset();
                } catch (e) {
                    console.log(e, 'Please fill the form!');
                }
            }
        }
    }

    private async _postEmailContact(email: string): Promise<void> {
        // NB in test the post body will need to look like this: { content: { emailAddress: email } }
        /* tslint:disable */
        const response = await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'PostContact',
            'post',
            {
                emailAddress: email,
                country: this.props.config.country,
                emarsysGroup: this.props.config.emarsysGroup
            }
            
        ) as any;
        
        this.responseMessage = response?.data.Message;
        this.setState({
            responseReceived: true,
            responseError: response?.data.Result === 'Error',
            responseSuccess: response?.data.Result !== 'Error',
            emailAddress: '',
            privacyPolicyChecked: false,
            reqError: false,
            eAValError: false,
            rCCError: false
        });

        if (response?.data.Result !== 'Error') {
            SetCookie(this.props.context.request.cookies, 'HIDE', this._newsletterCookieName, this.props.config.cookieAge || 30* 24 * 60 * 60)
        }
        /* tslint:enable */
    }

    private _clearValues(): void {
        if (this._PrivacyPolicyRef.current) {
            this._PrivacyPolicyRef.current.checked = false;
            const recaptchaWrapper = document.querySelector('.recaptcha');
            recaptchaWrapper && recaptchaWrapper.classList.toggle('newsletter-recapture-visable', true);
        }

        if (this._EmailAddressInputRef.current) {
            this._EmailAddressInputRef.current!.value = '';
        }
    }
}

export default CittaNewsletterSubscribe;
